import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { getBatchHeader } from '~/features/catalog-operations/services/catalog-operations.service';
import { useTenantAdmin } from '~/hooks/useTenantAdmin';

import { useTriggerDelivery } from '../hooks/use-trigger-delivery';
import type { DeliveryRunDetail, TriggerDeliveryRequest } from '../types/delivery-runs.types';
import { canRetryDelivery } from '../utils/derive-delivery-actions';
import { redeliveryBlockReason } from '../utils/redelivery-gate';

import { RedeliveryBlockedDialog, type RedeliveryBlockedReason } from './redelivery-blocked-dialog';

interface Props {
  run: DeliveryRunDetail;
  /** Catalog batch the run delivers, when the host knows it (wizard, Compare page). */
  batchId?: string | null | undefined;
  /** Follow the new run in place; default opens it on `/deliveries/$runId`. */
  onRunCreated?: ((runId: string) => void) | undefined;
  /** `rail` = ghost, icon-only below 1280px with a tooltip; `card` = outline, always labelled. */
  presentation?: 'rail' | 'card';
  /** Overrides the button label (the confirm dialog keeps its own copy). */
  label?: string;
}

/**
 * "Retry delivery" — re-triggers the whole delivery as a NEW run. Opens the
 * batch's delivery wizard in redeliver mode whenever the run's catalog batch is
 * known (host `batchId` or `source.enrichmentJobId`); runs with no batch keep the
 * one-click "redeliver everything" confirm. Gated by `canRetryDelivery` so every
 * surface offering it (control rail, products-unavailable card) agrees.
 *
 * Before opening the wizard it runs the shared `redeliveryBlockReason` gate against
 * the batch's current header, and shows a popup instead of navigating when the
 * retry can't start — the operator stays on this page.
 */
export function RetryDeliveryButton({
  run,
  batchId,
  onRunCreated,
  presentation = 'rail',
  label,
}: Props) {
  const { t } = useTranslation('deliveries');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTenantAdmin = useTenantAdmin();
  const retryDelivery = useTriggerDelivery(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [blockedReason, setBlockedReason] = useState<RedeliveryBlockedReason | null>(null);

  if (!canRetryDelivery(run)) return null;

  const text = label ?? t('controls.retryDelivery');
  const retryBatchId = batchId ?? run.source?.enrichmentJobId ?? null;

  // Default: open the new run on the console's run detail page.
  const openRun = (runId: string) => {
    if (onRunCreated) {
      onRunCreated(runId);
      return;
    }
    void navigate({ to: '/deliveries/$runId', params: { runId }, search: { tab: 'overview' } });
  };

  // Gate against the batch's live header (always refetched — it moves server-side),
  // then either open the wizard or explain in a popup why it can't start.
  const openRedeliverWizard = async (batchId: string) => {
    setChecking(true);
    try {
      const batch = await queryClient.fetchQuery({
        queryKey: queryKeys.catalogOperations.batchHeader(batchId),
        queryFn: () => getBatchHeader(batchId),
        staleTime: 0,
      });
      const reason = redeliveryBlockReason({ isTenantAdmin, run, batch });
      if (reason) {
        setBlockedReason(reason);
        return;
      }
      void navigate({
        to: '/catalog-operations/$batchId/deliver',
        params: { batchId },
        search: { redeliver: run.id },
      });
    } catch {
      setBlockedReason('batchMissing');
    } finally {
      setChecking(false);
    }
  };

  const onClick = () => {
    if (retryBatchId) {
      void openRedeliverWizard(retryBatchId);
      return;
    }
    setConfirmOpen(true);
  };

  const confirm = (
    <ConfirmDialog
      open={confirmOpen}
      testId="delivery-control-redeliver-dialog"
      onOpenChange={setConfirmOpen}
      title={t('controls.confirm.redeliverTitle')}
      description={t('controls.confirm.redeliverBody')}
      confirmLabel={t('controls.confirm.redeliverConfirm')}
      cancelLabel={t('controls.confirm.dismiss')}
      isPending={retryDelivery.isPending}
      onConfirm={() => {
        if (!run.connectionId) return;
        const sourceWorkflowId = run.source?.sourceWorkflowId;
        const body: TriggerDeliveryRequest = {
          forceRedeliver: true,
          ...(sourceWorkflowId ? { workflowId: sourceWorkflowId } : {}),
        };
        retryDelivery.mutate(
          { connectionId: run.connectionId, body },
          {
            onSuccess: (job) => {
              setConfirmOpen(false);
              openRun(job.id);
            },
          },
        );
      }}
    />
  );

  const blocked = (
    <RedeliveryBlockedDialog
      reason={blockedReason}
      onClose={() => {
        setBlockedReason(null);
      }}
    />
  );
  const busy = retryDelivery.isPending || checking;

  if (presentation === 'card') {
    return (
      <>
        <Button
          data-testid="delivery-control-retry-delivery-card-button"
          variant="outline"
          size="sm"
          className="cursor-pointer"
          isLoading={busy}
          onClick={onClick}
        >
          {!busy && <RefreshCw aria-hidden="true" />}
          {text}
        </Button>
        {confirm}
        {blocked}
      </>
    );
  }

  return (
    <>
      {/* Below 1280px the action collapses to an icon button; the tooltip (only
          shown at that width) carries the label instead. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid="delivery-control-retry-delivery-button"
            isLoading={busy}
            variant="ghost"
            size="sm"
            className="cursor-pointer"
            aria-label={text}
            onClick={onClick}
          >
            <RefreshCw aria-hidden="true" />
            <span className="hidden xl:inline">{text}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent className="xl:hidden">{text}</TooltipContent>
      </Tooltip>
      {confirm}
      {blocked}
    </>
  );
}
